/**
 * Legacy Adapters - Bridge between old cropper.service.ts and new CropService
 *
 * These functions provide backward-compatible wrappers around the new CropService,
 * allowing gradual migration of existing code.
 *
 * USAGE:
 * Replace imports from '@shared/services/cropper.service' with imports from
 * '@shared/services/crop/adapters' to start using the new unified logic
 * while maintaining the same API signatures.
 */

import type { Coordinates } from 'vue-advanced-cropper';
import type { SubjectImageDisplayModel, GroupImageDisplayModel } from '@shared/models/subjects/images';
import type { PackageItemImageNodeDisplayModel } from '@shared/models/packages/packageNodes';
import type { CartPackageImageNodeDisplayModel } from '@shared/models/carts/cartPackageImageNodes';
import type { PackageDisplayModel } from '@shared/models/packages/packagesV3';
import type { PackageItemDisplayModel } from '@shared/models/packages/packageItemsV3';
import type { Cartv2PackageItemDisplayModel } from '@shared/models/carts/cartPackages';
import { EntrySubjectGroupImageModel } from '@shared/models/entry/entry';
import { useOrganizationEditorStore } from '@apps/organization-editor/stores/organizationEditor.store';
import { WHOLE_DIGITAL_DOWNLOAD, SINGLE_DIGITAL_DOWNLOAD, isV3MultiImageDigitalDownload } from '@shared/models/products/productLookup';
import { computed, ref, watch } from 'vue';
import {
  type NormalizedCrop,
  type PixelCrop,
  type Dimensions,
  type NormalizedRotation,
  fromSoftCrop,
  normalizeRotation,
  parsePositionCoords,
  getNodeAspectRatio,
  shouldFlipAspectRatio,
} from './CropService';

// =============================================================================
// IMAGE LOADING (kept for compatibility, but could be moved to a separate util)
// =============================================================================

/**
 * Loads an image and returns it as an HTMLImageElement
 */
export async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'Anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

/**
 * Gets image dimensions from a URL
 */
export async function getImageDimensions(imageUrl: string): Promise<Dimensions> {
  const img = await loadImage(imageUrl);
  return {
    width: img.naturalWidth || img.width,
    height: img.naturalHeight || img.height,
  };
}

// =============================================================================
// LEGACY API ADAPTERS
// =============================================================================

/**
 * Adapter for: getCropDataFromSoftCropUsingThumbnail
 *
 * Converts a SubjectImage's soft crop to pixel coordinates using the thumbnail.
 * This is the most commonly used function in the old API.
 */
export async function getCropDataFromSoftCropUsingThumbnail(
  image: SubjectImageDisplayModel,
): Promise<PixelCrop & { width: number; height: number }> {
  const dims = await getImageDimensions(image.imageThumbnailUrl);

  const normalized = fromSoftCrop({
    cropX: image.cropX ?? 100,
    cropY: image.cropY ?? 100,
    xMid: image.xMid ?? 50,
    yMid: image.yMid ?? 50,
    rotation: image.rotation ?? 0,
  });

  // Calculate in original image space — vue-advanced-cropper handles rotation
  // internally via defaultTransforms, so coordinates stay in original space.
  const cropWidth = (normalized.widthPct / 100) * dims.width;
  const cropHeight = (normalized.heightPct / 100) * dims.height;
  const centerX = (normalized.centerX / 100) * dims.width;
  const centerY = (normalized.centerY / 100) * dims.height;

  return {
    left: centerX - cropWidth / 2,
    top: centerY - cropHeight / 2,
    width: cropWidth,
    height: cropHeight,
  };
}

/**
 * Adapter for: getCropDataFromSoftCropImageUsingImage
 *
 * Same as above but uses the full image URL instead of thumbnail.
 */
export async function getCropDataFromSoftCropImageUsingImage(
  image: SubjectImageDisplayModel,
): Promise<PixelCrop & { width: number; height: number }> {
  const dims = await getImageDimensions(image.imageUrl);

  const normalized = fromSoftCrop({
    cropX: image.cropX ?? 100,
    cropY: image.cropY ?? 100,
    xMid: image.xMid ?? 50,
    yMid: image.yMid ?? 50,
    rotation: image.rotation ?? 0,
  });

  // Calculate in original image space — vue-advanced-cropper handles rotation
  // internally via defaultTransforms, so coordinates stay in original space.
  const cropWidth = (normalized.widthPct / 100) * dims.width;
  const cropHeight = (normalized.heightPct / 100) * dims.height;
  const centerX = (normalized.centerX / 100) * dims.width;
  const centerY = (normalized.centerY / 100) * dims.height;

  return {
    left: centerX - cropWidth / 2,
    top: centerY - cropHeight / 2,
    width: cropWidth,
    height: cropHeight,
  };
}

/**
 * Adapter for: getOrderXmlCropDimensions
 *
 * Converts cropper coordinates to Order XML format string.
 */
export function getOrderXmlCropDimensions(
  cropCoordinates: { left: number; top: number; width: number; height: number },
  imageMetaData: { width: number; height: number },
  rotation: NormalizedRotation,
): string {
  let imgWidth = imageMetaData.width;
  let imgHeight = imageMetaData.height;

  // Swap width and height if rotated by 90 or 270 degrees
  if (rotation === 90 || rotation === 270) {
    imgWidth = imageMetaData.height;
    imgHeight = imageMetaData.width;
  }

  const newCropX = ((cropCoordinates.width / imgWidth) * 100).toFixed(0);
  const newCropY = ((cropCoordinates.height / imgHeight) * 100).toFixed(0);
  const newXMid = (((cropCoordinates.left + cropCoordinates.width / 2) / imgWidth) * 100).toFixed(0);
  const newYMid = (((cropCoordinates.top + cropCoordinates.height / 2) / imgHeight) * 100).toFixed(0);

  return `${newXMid} ${newYMid} ${newCropX} ${newCropY}`;
}

/**
 * Adapter for: convertCoordinates
 *
 * Parses positionCoords string from package nodes.
 */
export function convertCoordinates(positionCoords: string): {
  left: number;
  top: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
} {
  return parsePositionCoords(positionCoords);
}

/**
 * Adapter for: aspectRatioFromPackageNode
 *
 * Gets the aspect ratio from a package item image node.
 */
export function aspectRatioFromPackageNode(node: PackageItemImageNodeDisplayModel, invert: boolean): number {
  const { width, height } = parsePositionCoords(node.positionCoords);
  return getNodeAspectRatio(width, height, invert);
}

/**
 * Adapter for: isNodeFlipped
 *
 * Determines if the aspect ratio should be inverted for a cart node.
 * This is a FIXED version of the original buggy implementation.
 */
export function isNodeFlipped(
  cartNode: CartPackageImageNodeDisplayModel,
  itemNode: PackageItemImageNodeDisplayModel,
  softCrop?: PixelCrop,
): boolean {
  const { width: nodeWidth, height: nodeHeight } = parsePositionCoords(itemNode.positionCoords);
  const nodeRatio = nodeWidth / nodeHeight;

  // If we have frontend crop dimensions, use those
  const crop = cartNode.frontendCropDimensions || softCrop;
  if (crop) {
    const cropRatio = crop.width / crop.height;
    return shouldFlipAspectRatio(cropRatio, nodeRatio);
  }

  return false;
}

/**
 * Adapter for: maintainCropCenter
 *
 * Creates a function that maintains the crop center when resizing.
 */
export function maintainCropCenter(top: number, left: number, width: number, height: number) {
  const center = {
    left: left + width / 2,
    top: top + height / 2,
  };
  return ({ coordinates }: { coordinates: { width: number; height: number } }) => ({
    left: center.left - coordinates.width / 2,
    top: center.top - coordinates.height / 2,
  });
}

/**
 * Adapter for: maintainCropCenterFromCoords
 *
 * Wrapper that handles rotation for maintainCropCenter.
 */
export function maintainCropCenterFromCoords(coords: PixelCrop, rotation: number = 0) {
  const normalized = normalizeRotation(rotation);
  const swap = normalized === 90 || normalized === 270;

  return swap
    ? maintainCropCenter(coords.top, coords.left, coords.height, coords.width)
    : maintainCropCenter(coords.top, coords.left, coords.width, coords.height);
}

// =============================================================================
// ADDITIONAL LEGACY TYPES AND FUNCTIONS
// =============================================================================

/**
 * Legacy interface for dimensions
 */
export interface IDimensions {
  width: number;
  height: number;
  ratio?: number;
}

/**
 * Legacy interface for crop position (soft crop)
 */
export interface ICropPosition {
  cropX: number;
  cropY: number;
  xMid: number;
  yMid: number;
}

/**
 * Legacy interface for crop coordinates
 */
export interface ICropCoords extends IDimensions {
  left: number;
  top: number;
  bottom?: number;
  right?: number;
}

/**
 * Get image metadata (dimensions) from URL
 */
export async function getImageMetaData(imageThumbnailUrl: string): Promise<IDimensions> {
  const newImg = await loadImage(imageThumbnailUrl);
  return {
    height: newImg.height,
    width: newImg.width,
  };
}

/**
 * Adapter for: getCropDataFromCropPosition
 *
 * Converts crop position (soft crop) to pixel coordinates.
 */
export async function getCropDataFromCropPosition(o: ICropPosition, src: string): Promise<ICropCoords> {
  const { cropX, cropY, xMid, yMid } = o;
  const { width: imageWidth, height: imageHeight } = await loadImage(src);

  const cropWidth = imageWidth * (cropX / 100);
  const cropHeight = imageHeight * (cropY / 100);

  const cropCenterX = imageWidth * (xMid / 100);
  const cropCenterY = imageHeight * (yMid / 100);

  const cropLeft = cropCenterX - cropWidth / 2;
  const cropTop = cropCenterY - cropHeight / 2;

  return {
    left: cropLeft,
    top: cropTop,
    width: cropWidth,
    height: cropHeight,
  };
}

/**
 * Adapter for: getCropDataFromOrderCropPosition
 *
 * Parses an order XML crop position string to ICropPosition.
 */
export function getCropDataFromOrderCropPosition(cropPosition: string): ICropPosition {
  const [xMid, yMid, cropX, cropY] = cropPosition.split(' ').map((m) => Number.parseFloat(m));
  return {
    cropX,
    cropY,
    xMid,
    yMid,
  };
}

/**
 * Adapter for: convertCropToSoftCropPercentages
 *
 * Converts cropper coordinates back to soft crop percentages.
 */
export function convertCropToSoftCropPercentages(coordinates: Coordinates, image: IDimensions) {
  const cropX = Math.round((coordinates.width / image.width) * 100);
  const cropY = Math.round((coordinates.height / image.height) * 100);
  const yMid = Math.round(((coordinates.top + coordinates.height / 2) / image.height) * 100);
  const xMid = Math.round(((coordinates.left + coordinates.width / 2) / image.width) * 100);

  return {
    cropX,
    cropY,
    xMid,
    yMid,
  };
}

/**
 * Adapter for: getAspectRatio
 *
 * Gets aspect ratio from dimensions.
 */
export function getAspectRatio(dims: IDimensions) {
  const { height, width } = dims;
  return {
    height,
    width,
    ratio: height / width,
  };
}

/**
 * Adapter for: allowCrop
 *
 * Checks if cropping is allowed for a package/item.
 * Excludes digital downloads (legacy and V3 multi-image) from cropping.
 */
export function allowCrop(
  pkg: PackageDisplayModel,
  item: PackageItemDisplayModel | Cartv2PackageItemDisplayModel,
  forceAllow = false,
): boolean {
  const isDigitalDownload = item.productCode === WHOLE_DIGITAL_DOWNLOAD || item.productCode === SINGLE_DIGITAL_DOWNLOAD;

  if (isDigitalDownload) return false;

  // RSPO (V3) always allows cropping (the toggle was removed); legacy honors the studio job preference.
  return forceAllow || pkg.allowConsumerCrop;
}

/**
 * Check if an image is a group image
 */
const isGroupImageFn = (
  image: EntrySubjectGroupImageModel | GroupImageDisplayModel,
): image is GroupImageDisplayModel => {
  return (image as GroupImageDisplayModel).groupId !== undefined;
};

/**
 * Adapter for: useImageData
 *
 * Composable for getting image data and dimensions.
 */
export function useImageData(image: EntrySubjectGroupImageModel | GroupImageDisplayModel) {
  if (!image) {
    console.warn('No image provided to useImageData');
  }

  const isIndividualImage = computed(() => !isGroupImageFn(image) && image.imageLookup?.type === 'hosted-individual');
  const isGroupImage = computed(() => isGroupImageFn(image) || image.imageLookup?.type === 'hosted-group');

  // Fetch image dimensions dynamically
  const imageDimensions = ref<IDimensions | null>(null);
  const loadingDimensions = ref(false);
  const dimensionError = ref<Error | null>(null);

  const fetchImageDimensions = async () => {
    if (!image?.imageThumbnailUrl) {
      console.warn('No image thumbnail URL available');
      return;
    }

    loadingDimensions.value = true;
    dimensionError.value = null;

    try {
      imageDimensions.value = await getImageMetaData(image.imageThumbnailUrl);
    } catch (error) {
      dimensionError.value = error instanceof Error ? error : new Error('Failed to load image dimensions');
      console.error('Error fetching image dimensions:', dimensionError.value);
    } finally {
      loadingDimensions.value = false;
    }
  };

  // Auto-fetch dimensions when image changes
  watch(
    () => image?.imageThumbnailUrl,
    () => fetchImageDimensions(),
    { immediate: true },
  );

  const aspectRatios = {
    vertical5x7: { min: 0.7, max: 0.73 },
    horizontal7x5: { min: 1.37, max: 1.43 },
    horizontal10x8: { min: 1.23, max: 1.28 },
  };

  const isLandscape = computed(() => {
    const dims = imageDimensions.value;
    return dims ? dims.width > dims.height : false;
  });

  const isPortrait = computed(() => {
    const dims = imageDimensions.value;
    return dims ? dims.width < dims.height : false;
  });

  const isInVertical5x7Range = computed(() => {
    const dims = imageDimensions.value;
    if (dims) {
      const aspectRatio = dims.width / dims.height;
      return aspectRatio >= aspectRatios.vertical5x7.min && aspectRatio <= aspectRatios.vertical5x7.max;
    }
    return false;
  });

  const isInHorizontal7x5Range = computed(() => {
    const dims = imageDimensions.value;
    if (dims) {
      const aspectRatio = dims.width / dims.height;
      return aspectRatio >= aspectRatios.horizontal7x5.min && aspectRatio <= aspectRatios.horizontal7x5.max;
    }
    return false;
  });

  const isInHorizontal10x8Range = computed(() => {
    const dims = imageDimensions.value;
    if (dims) {
      const aspectRatio = dims.width / dims.height;
      return aspectRatio >= aspectRatios.horizontal10x8.min && aspectRatio <= aspectRatios.horizontal10x8.max;
    }
    return false;
  });

  const cropRatio = computed(() => {
    if (!imageDimensions.value) return null;
    return imageDimensions.value.width / imageDimensions.value.height;
  });

  return {
    isIndividualImage,
    isGroupImage,
    isLandscape,
    isPortrait,
    isInVertical5x7Range,
    isInHorizontal7x5Range,
    isInHorizontal10x8Range,
    imageDimensions,
    cropRatio,
    loadingDimensions,
    dimensionError,
    fetchImageDimensions,
  };
}

/**
 * Adapter for: getComputedAspectRatioAsync
 *
 * Computes the aspect ratio based on image type and organization settings.
 */
export async function getComputedAspectRatioAsync(
  image: EntrySubjectGroupImageModel | GroupImageDisplayModel,
): Promise<number> {
  const data = useImageData(image);

  // Wait until imageDimensions is available
  if (!data.imageDimensions.value) {
    await data.fetchImageDimensions();
  }

  const organizationStore = useOrganizationEditorStore();
  const isSportsOrganization = organizationStore.isSportsOrganization;

  if (data.isIndividualImage.value && data.isPortrait.value) {
    if (data.isInVertical5x7Range.value && isSportsOrganization) return 5 / 7;
    return 8 / 10;
  }
  if (data.isIndividualImage.value && data.isLandscape.value) {
    if (data.isInHorizontal7x5Range.value && isSportsOrganization) return 7 / 5;
    return 10 / 8;
  }
  if (data.isGroupImage.value && data.isPortrait.value) {
    if (data.isInVertical5x7Range.value) return 5 / 7;
    return 8 / 10;
  }
  if (data.isGroupImage.value && data.isLandscape.value) {
    if (data.isInHorizontal10x8Range.value) return 10 / 8;
    return 7 / 5;
  }

  console.warn('Could not determine aspect ratio, defaulting to 8/10');
  return 8 / 10;
}

// Re-export the normalizeRotation for compatibility
export { normalizeRotation };
